import { createFileRoute } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, useEffect, useRef } from "react";
import {
  listDrafts,
  getOrCreateDraft,
  saveDraft,
  completeDraft,
  deleteDraft,
} from "@/lib/guided-journal.functions";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";
import { BookHeart, ArrowLeft, Check, Trash2, Loader2 } from "lucide-react";
import { notifyActivityChange } from "@/lib/activity-events";
import { readStoredAuthSession, resolveAuthSession } from "@/lib/auth-session";
import { JournalGuide } from "@/components/JournalGuide";

export const Route = createFileRoute("/guided-journal")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Guided Journal — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Reflective prompts with autosaved drafts you can resume anytime.",
      },
      { property: "og:title", content: "Guided Journal — Recovery Trauma™" },
      { property: "og:description", content: "Reflective prompts with autosaved drafts you can resume anytime." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/guided-journal" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/guided-journal" }],
  }),
  component: withMemberGate(GuidedJournalPage, "Guided Journal"),
});

const PROMPTS = [
  {
    slug: "safety-today",
    title: "Where do I feel safe today?",
    body: "Name a place, a person, or a sensation in your body where you feel even a small amount of safety. Describe it in detail.",
  },
  {
    slug: "noticing-body",
    title: "What is my body telling me right now?",
    body: "Slowly scan from head to toe. Where is there tension, warmth, numbness, or ease? Write without judgment.",
  },
  {
    slug: "gentle-truth",
    title: "A gentle truth I am ready to hold",
    body: "Something true about your journey that doesn't need fixing — only acknowledgment.",
  },
  {
    slug: "small-win",
    title: "A small win from this week",
    body: "Healing is not linear. Name one small thing — a boundary, a breath, a moment of rest — that mattered.",
  },
  {
    slug: "inner-child",
    title: "A letter to my younger self",
    body: "What would you tell the version of you that first carried this? Write with the warmth you wish you'd received.",
  },
  {
    slug: "release",
    title: "What I am ready to release",
    body: "Name a thought, story, or weight you no longer want to carry. You don't have to know how — only that you are willing.",
  },
];

// Ensure we have a fresh session token before calling a protected server fn.
// On mobile (especially Safari/Chrome backgrounded tabs) the cached session
// may have expired; getSession() refreshes it automatically.
async function ensureSession() {
  const session = await resolveAuthSession(supabase, 1_200);
  if (!session?.user.id) {
    throw new Error("Please sign in again to save your reflection.");
  }
}

function GuidedJournalPage() {
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const prompt = PROMPTS.find((p) => p.slug === activeSlug);

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <div
        className="mb-8 rounded-[26px] border border-border/60 p-5 shadow-sm sm:p-7"
        style={{
          backgroundImage:
            "linear-gradient(150deg, var(--qa-peach-tint) 0%, var(--qa-gold-tint) 48%, var(--card) 100%)",
        }}
      >
        <h1 className="font-serif text-3xl text-foreground">Guided Journal</h1>
        <p className="mt-1 text-sm text-foreground/75">
          Choose a prompt. Your draft autosaves — return anytime.
        </p>
      </div>

<JournalGuide journal="guided" className="mb-6" />

      {prompt ? (
        <DraftEditor prompt={prompt} onBack={() => setActiveSlug(null)} />
      ) : (
        <PromptList onSelect={setActiveSlug} />
      )}
    </div>
  );
}

function PromptList({ onSelect }: { onSelect: (slug: string) => void }) {
  const fetchDrafts = useServerFn(listDrafts);
  const { data } = useQuery({
    queryKey: ["journal-drafts"],
    queryFn: fetchDrafts,
  });

  const drafts = data?.drafts ?? [];
  const activeDrafts = drafts.filter((d) => !d.completed_at);

  return (
    <div className="space-y-8">
      {activeDrafts.length > 0 && (
        <section>
          <h2 className="mb-3 font-serif text-lg text-foreground">
            Resume a draft
          </h2>
          <div className="space-y-2">
            {activeDrafts.map((d) => (
              <button
                key={d.id}
                onClick={() => onSelect(d.prompt_slug)}
                className="flex w-full items-center justify-between rounded-xl border border-gold/30 bg-card p-4 text-start transition-colors hover:border-gold/60"
              >
                <div>
                  <p className="font-serif text-base text-foreground">
                    {d.prompt_title}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Last edited{" "}
                    {new Date(d.updated_at).toLocaleString("en-US", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                    {d.content
                      ? ` · ${d.content.trim().split(/\s+/).filter(Boolean).length} words`
                      : " · empty"}
                  </p>
                </div>
                <ArrowLeft className="h-4 w-4 rotate-180 text-gold" />
              </button>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="mb-3 font-serif text-lg text-foreground">
          Choose a prompt
        </h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {PROMPTS.map((p) => {
            const hasDraft = activeDrafts.some((d) => d.prompt_slug === p.slug);
            return (
              <button
                key={p.slug}
                onClick={() => onSelect(p.slug)}
                className="group flex flex-col rounded-2xl border border-border/50 bg-card p-5 text-start transition-all hover:border-lavender/60 hover:shadow-sm"
              >
                <div className="mb-2 flex items-center gap-2">
                  <BookHeart className="h-4 w-4 text-gold" />
                  {hasDraft && (
                    <span className="rounded-full bg-blush/60 px-2 py-0.5 text-[10px] font-medium text-foreground">
                      In progress
                    </span>
                  )}
                </div>
                <p className="font-serif text-base text-foreground">
                  {p.title}
                </p>
                <p className="mt-2 text-xs text-muted-foreground">{p.body}</p>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

type Notice = { type: "info" | "error" | "success"; message: string } | null;

function DraftEditor({
  prompt,
  onBack,
}: {
  prompt: (typeof PROMPTS)[number];
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const fetchOrCreate = useServerFn(getOrCreateDraft);
  const save = useServerFn(saveDraft);
  const complete = useServerFn(completeDraft);
  const remove = useServerFn(deleteDraft);

  // Track auth so taps are never silently dropped on mobile.
  const [authReady, setAuthReady] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const isAuthed = !!session;
  useEffect(() => {
    let mounted = true;
    const storedSession = readStoredAuthSession();
    if (storedSession) {
      setSession(storedSession);
      setAuthReady(true);
    }
    resolveAuthSession(supabase).then((nextSession) => {
      if (!mounted) return;
      setSession(nextSession ?? null);
      setAuthReady(true);
    });
    const handler = (e: Event) => {
      const next = (e as CustomEvent).detail?.session as Session | null | undefined;
      setSession(next ?? null);
      setAuthReady(true);
    };
    window.addEventListener("rt-auth-change", handler);
    return () => {
      mounted = false;
      window.removeEventListener("rt-auth-change", handler);
    };
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["journal-draft", prompt.slug],
    queryFn: () =>
      fetchOrCreate({
        data: { prompt_slug: prompt.slug, prompt_title: prompt.title },
      }),
    enabled: authReady && isAuthed,
  });

  const draft = data?.draft;
  const [content, setContent] = useState("");
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (draft && !initialized.current) {
      setContent(draft.content || "");
      initialized.current = true;
    }
  }, [draft]);

  const saveMutation = useMutation({
    mutationFn: async (vars: { id: string; content: string }) => {
      await ensureSession();
      return save({ data: vars });
    },
    onSuccess: () => {
      setSavedAt(new Date());
      setNotice(null);
    },
    onError: (err: unknown) => {
      setNotice({
        type: "error",
        message:
          err instanceof Error
            ? err.message
            : "Couldn't autosave. Check your connection and try again.",
      });
    },
  });

  useEffect(() => {
    if (!draft || !initialized.current) return;
    if (content === (draft.content || "")) return;
    const t = setTimeout(() => {
      saveMutation.mutate({ id: draft.id, content });
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, draft?.id]);

  const completeMutation = useMutation({
    mutationFn: async (vars: { id: string }) => {
      await ensureSession();
      return complete({ data: vars });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["journal-drafts"] });
      qc.invalidateQueries({ queryKey: ["journal-entries"] });
      notifyActivityChange("journal");
      onBack();
    },
    onError: (err: unknown) => {
      setNotice({
        type: "error",
        message:
          err instanceof Error
            ? err.message
            : "Couldn't save your reflection. Please try again.",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (vars: { id: string }) => {
      await ensureSession();
      return remove({ data: vars });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["journal-drafts"] });
      onBack();
    },
    onError: (err: unknown) => {
      setNotice({
        type: "error",
        message:
          err instanceof Error
            ? err.message
            : "Couldn't discard draft. Please try again.",
      });
    },
  });

  const handleSaveReflection = () => {
    if (!content.trim() || completeMutation.isPending) return;
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (!authReady) {
      setNotice({ type: "info", message: "Just a moment — preparing your journal…" });
      window.setTimeout(() => handleSaveReflection(), 400);
      return;
    }
    if (!isAuthed) {
      setNotice({
        type: "error",
        message: "Please sign in to save your reflection.",
      });
      return;
    }
    if (!draft) return;
    completeMutation.mutate({ id: draft.id });
  };

  const handleDiscard = () => {
    if (!draft) return;
    if (!authReady) {
      setNotice({ type: "info", message: "Just a moment — preparing your journal…" });
      window.setTimeout(() => handleDiscard(), 400);
      return;
    }
    if (confirm("Discard this draft?")) {
      deleteMutation.mutate({ id: draft.id });
    }
  };

  if (authReady && !isAuthed) {
    return (
      <div className="rounded-2xl border border-gold/30 bg-card p-8 text-center">
        <p className="font-serif text-lg text-foreground">
          Please sign in to use the Guided Journal
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Your reflections are stored privately to your account.
        </p>
        <button
          onClick={onBack}
          className="mt-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          All prompts
        </button>
      </div>
    );
  }

  if (!authReady || isLoading || !draft) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;

  return (
    <div>
      <button
        onClick={onBack}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        All prompts
      </button>

      <div className="mb-6 rounded-2xl border border-gold/30 bg-card p-6">
        <p className="font-serif text-xl text-foreground">{prompt.title}</p>
        <p className="mt-2 text-sm text-muted-foreground">{prompt.body}</p>
      </div>

      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={`${prompt.title}\n\n${prompt.body}\n\nStart writing here — your words save themselves.`}
        rows={14}
        className="w-full resize-none rounded-2xl border border-border/50 bg-card p-5 font-serif text-base leading-relaxed text-foreground placeholder:whitespace-pre-line placeholder:text-muted-foreground focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/20"
      />

      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>{wordCount} words</span>
        <span>
          {saveMutation.isPending
            ? "Saving…"
            : savedAt
              ? `Saved ${savedAt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
              : "Autosaves as you type"}
        </span>
      </div>

      {notice && (
        <div
          role="status"
          className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
            notice.type === "error"
              ? "border-destructive/40 bg-destructive/10 text-destructive"
              : notice.type === "success"
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                : "border-gold/40 bg-gold/10 text-foreground"
          }`}
        >
          {notice.message}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={handleDiscard}
          disabled={deleteMutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4" />
          {deleteMutation.isPending ? "Discarding…" : "Discard draft"}
        </button>
        <button
          type="button"
          onClick={(ev) => {
            ev.preventDefault();
            handleSaveReflection();
          }}
          disabled={!content.trim() || completeMutation.isPending}
          className="relative z-20 inline-flex min-h-[48px] touch-manipulation items-center justify-center gap-1.5 rounded-lg bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50 sm:min-h-0 sm:py-2"
        >
          <Check className="h-4 w-4" />
          {completeMutation.isPending ? "Saving with care…" : "Save Reflection"}
        </button>
      </div>
    </div>
  );
}
